import { Alert, Icon, Typography } from "@/components/atoms";
import { flightTerminalLabel } from "@/helpers/bookingHelpers";
import { FlightDetails } from "@/redux/apis/clientFlight/types";
import { formatDurationWords } from "./flightPickup";

/**
 * Where the car is taking them, as the travel sentence names it — "Sydney Terminal 1", or just
 * "Sydney" when the flight names no terminal yet. The CITY rather than the airport's full name, so the
 * sentence stays a sentence ("Travel time to Sydney Terminal 1" beats "…to Sydney Airport Terminal 1").
 */
const departureLabel = (flight: FlightDetails): string | null => {
  const airport = flight.departure?.airport;
  const place = airport?.city || airport?.name;
  if (!place) return null;
  const terminal = flightTerminalLabel(flight.departure?.terminal);
  return terminal ? `${place} ${terminal}` : place;
};

/**
 * SUGGESTED PICKUP — the card above the time picker on an airport drop-off (DEV-1487, DSN-611).
 *
 * The time itself is pre-filled into the picker rather than restated here: it is EDITABLE, and a card
 * showing a time beside a field holding a different one is the disclosure defect the arrival-side card
 * spent DEV-1510 removing. What this card carries is the WORKING — the two subtractions that produced
 * the suggestion — so a client can trust it or override it knowingly (user stories 4 and 5).
 *
 * WHEN THE FLIGHT LEAVES is not restated either. `FlightDetailsCard` sits directly above in departure
 * mode and its footer already reads `Departs 1:25 PM • Terminal 1`; printing the same line again here
 * is the same fact twice in adjacent boxes.
 *
 * ## It names no flight class
 *
 * The Figma copy reads "Recommended arrival for an INTERNATIONAL flight is 3 hours before departure".
 * That word is not printed. LEAD TIME arrives as a number of minutes, and the international/domestic
 * split behind it is two operator-configurable global keys — an operator on 120/90 would have this
 * card asserting "international" about a domestic flight, from a number that cannot distinguish them.
 * The server knows the class; the client only knows the duration, so the duration is what it states.
 *
 * ## The travel sentence is optional by contract
 *
 * `departureTravelSeconds` is null whenever the pickup had no coordinates or the distance provider
 * failed (contract §1). The suggestion is still correct — it is then lead-only — so the card drops the
 * sentence rather than the card.
 */
export const SuggestedPickupCard = ({ flight, flightNumber }: { flight: FlightDetails; flightNumber: string }) => {
  const place = departureLabel(flight);

  return (
    <Alert
      // The AMBER callout, per DSN-611 and the mobile design. Not `info`: the outlined blue box reads as
      // the neutral furniture this panel is already full of — beside the payment box it disappeared —
      // and this is the one thing on the step actively proposing a value the client should look at.
      type="warning"
      // The idea/lightbulb, replacing the `warning` type's own hazard triangle. The amber is emphasis
      // here, not alarm: nothing is wrong, we are suggesting something.
      //
      // `LampCharge` rather than iconsax's `LampOn`: only the former is in this project's `IconSax`
      // registry, and an unregistered name renders NOTHING while logging "Icon … not found".
      customIcon={<Icon name="LampCharge" variant="Bold" size="lg" className="shrink-0 self-start text-warning" />}
      message={
        <span className="flex flex-col gap-1 text-left">
          <Typography variant="action">{`Suggested pickup time (${flightNumber})`}</Typography>
          {flight.departureLeadMinutes != null && (
            <Typography variant="small-regular" className="font-medium">
              {`Recommended arrival is ${formatDurationWords(flight.departureLeadMinutes)} before departure.`}
            </Typography>
          )}
          {flight.departureTravelSeconds != null && place && (
            <Typography variant="small-regular" className="font-medium">
              {`Travel time to ${place} is around ${formatDurationWords(flight.departureTravelSeconds / 60)}.`}
            </Typography>
          )}
        </span>
      }
    />
  );
};
